import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Globe, Shield, Zap, Lock, 
  Activity, Car, Cpu, BarChart3, CheckCircle2 
} from 'lucide-react';
import LiveFeedTerminal from '../components/LiveFeedTerminal';

const Home = () => {
  return (
    <div className="bg-white min-h-screen font-sans selection:bg-indigo-100 selection:text-indigo-900">
      
      {/* 1. HERO SECTION */}
      <section className="relative pt-40 pb-24 lg:pt-52 lg:pb-40 overflow-hidden">
        {/* Background Blobs */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full z-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-32 left-40 w-[600px] h-[600px] bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wide mb-10 shadow-sm backdrop-blur-md hover:scale-105 transition-transform cursor-default">
            <span className="flex h-2 w-2 rounded-full bg-blue-600 animate-pulse"></span> Search Technology Platform
          </div>
          
          <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight mb-8 leading-[1.1]">
            The Infrastructure of <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
              Search Intent.
            </span>
          </h1>
          
          <p className="text-lg md:text-2xl text-slate-600 mb-14 max-w-3xl mx-auto leading-relaxed font-medium">
            Searchy.io powers discovery for the world's leading advertisers. We bridge the gap between user curiosity and premium commercial results.
          </p>

          {/* FIX: Cleaned up Tech Demo Card (Removed the inset-1 div) */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200 p-4 flex flex-col md:flex-row items-center gap-6 hover:border-blue-300 transition-all duration-300">
              
              <div className="hidden md:flex items-center gap-3 px-4 border-r border-slate-200">
                <Globe className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-bold text-slate-700 whitespace-nowrap">Global Feed</span>
              </div>
              
              <div className="flex-grow w-full md:w-auto flex items-center justify-center md:justify-start gap-3 bg-slate-50 md:bg-transparent p-3 md:p-0 rounded-lg">
                 <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                 </span>
                 <span className="text-slate-500 font-mono text-sm">System Operational. Indexing active.</span>
              </div>

              <Link to="/about" className="w-full md:w-auto bg-slate-900 text-white px-8 py-3.5 rounded-xl font-bold hover:bg-blue-600 transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap">
                Explore Tech <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm font-semibold text-slate-500">
              <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-blue-500"/> Brand Safe</span>
              <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-yellow-500"/> &lt; 50ms Latency</span>
              <span className="flex items-center gap-2"><Lock className="h-4 w-4 text-green-500"/> SSL Encrypted</span>
            </div>
          </div>
        </div>
      </section>

      {/* 2. STATS BAR */}
      <div className="max-w-7xl mx-auto px-4 -mt-10 relative z-20">
        <div className="bg-white/40 backdrop-blur-xl border border-white/50 rounded-3xl shadow-xl p-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
           {[
            { label: 'Daily Impressions', value: '15M+' },
            { label: 'Active Vertical Feeds', value: '45+' },
            { label: 'Traffic Quality', value: '99.9%' },
            { label: 'Latency', value: '<50ms' },
          ].map((stat, i) => (
            <div key={i} className="relative">
               {i !== 3 && <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-12 bg-slate-200/60"></div>}
              <div className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-1 tracking-tight">{stat.value}</div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 3. BENTO GRID */}
      <section className="py-32 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div className="max-w-2xl">
                <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Core Verticals</h2>
                <p className="text-slate-600 text-lg">We specialize in high-intent categories, delivering verified results across the most competitive markets.</p>
            </div>
            <Link to="/about" className="text-blue-600 font-bold hover:text-blue-700 flex items-center gap-2 group">
                View All Capabilities <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[340px]">
            {/* Health Card */}
            <Link to="/about" className="md:col-span-2 relative group overflow-hidden rounded-[2rem] bg-white border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-emerald-50 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity transform group-hover:scale-110 duration-700">
                 <Activity className="h-64 w-64 text-teal-600" />
              </div>
              <div className="relative p-10 h-full flex flex-col justify-between z-10">
                <div>
                  <div className="h-14 w-14 rounded-2xl bg-teal-100 text-teal-600 flex items-center justify-center mb-6 shadow-inner">
                    <Activity className="h-7 w-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Health Intelligence</h3>
                  <p className="text-slate-500 text-lg max-w-md leading-relaxed">
                    Verified medical data streams and supplement trends.
                  </p>
                </div>
                <div className="flex items-center gap-3 text-teal-700 font-bold bg-teal-50 w-fit px-5 py-2 rounded-full group-hover:bg-teal-600 group-hover:text-white transition-all">
                  View Specs <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </Link>

            {/* Auto Card */}
            <Link to="/about" className="md:row-span-2 relative group overflow-hidden rounded-[2rem] bg-slate-900 text-white shadow-xl hover:shadow-2xl transition-all duration-500">
               <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-slate-900"></div>
               <div className="relative p-10 h-full flex flex-col z-10">
                  <div className="h-14 w-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center mb-8 shadow-lg shadow-blue-500/30">
                    <Car className="h-7 w-7" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4">Automotive<br/>Marketplace</h3>
                  <p className="text-slate-300 mb-10 text-lg leading-relaxed">
                    Real-time inventory data for high-value leads.
                  </p>
                  <div className="mt-auto space-y-4">
                    {['EV Pricing Feeds', 'Insurance Data', 'Dealership locators'].map((item, i) => (
                      <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/5">
                        <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                        <span className="text-base font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
               </div>
            </Link>

            {/* Tech Card */}
            <Link to="/about" className="relative group overflow-hidden rounded-[2rem] bg-white border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-500">
              <div className="relative p-10 h-full flex flex-col justify-between">
                <div>
                   <div className="h-14 w-14 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center mb-6">
                    <Cpu className="h-7 w-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Tech & Software</h3>
                  <p className="text-slate-500">SaaS discovery intent data.</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                    <ArrowRight className="h-5 w-5" />
                </div>
              </div>
            </Link>

             {/* Analytics Card */}
             <Link to="/about" className="relative group overflow-hidden rounded-[2rem] bg-white border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-500">
              <div className="relative p-10 h-full flex flex-col justify-between">
                <div>
                   <div className="h-14 w-14 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center mb-6">
                    <BarChart3 className="h-7 w-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Data & Analytics</h3>
                  <p className="text-slate-500">Transparent reporting.</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-all">
                    <ArrowRight className="h-5 w-5" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. FEATURE SPLIT */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-20">
            <LiveFeedTerminal />
            <div className="w-full md:w-1/2">
              <div className="inline-block px-4 py-2 bg-blue-50 text-blue-700 font-bold rounded-full text-sm mb-6">Real-Time Core</div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6 leading-tight">
                Live Intent Stream™
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Our proprietary engine analyzes millions of anonymous search queries in real-time. We identify high-intent users at the exact moment of discovery without relying on stale data.
              </p>
              
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-600" />
                    <span className="text-slate-700 font-medium">Filters bot traffic instantly</span>
                </li>
                <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-600" />
                    <span className="text-slate-700 font-medium">Classifies intent by vertical</span>
                </li>
                <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-600" />
                    <span className="text-slate-700 font-medium">Matches bids dynamically</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CTA SECTION */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto bg-gradient-to-r from-blue-600 to-indigo-700 rounded-[3rem] p-12 md:p-24 text-center relative overflow-hidden shadow-2xl">
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-8 tracking-tight">Ready to scale your reach?</h2>
            
            <p className="text-blue-100 text-xl mb-6 max-w-3xl mx-auto font-medium leading-relaxed">
              Whether you are a publisher looking to monetize or an advertiser seeking high-intent traffic, our infrastructure is built for your growth. 
            </p>
            <p className="text-blue-100/80 text-lg mb-12 max-w-2xl mx-auto leading-relaxed">
               Experience transparent reporting, dedicated support, and access to premium global feeds without the complexity.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-5">
              <Link to="/contact" className="bg-white text-blue-700 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-blue-50 transition shadow-lg hover:scale-105 transform duration-200">
                Partner With Us
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;